import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { AuthFrame } from "@/components/marketing/auth-frame";
import { Button } from "@/components/ui/button";
import { CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { nextUrl, oauthClient } from "@/lib/host/oauth-client";

export const Route = createFileRoute("/oauth/sign-in")({ component: OAuthSignInPage });

/** Follows the server to the next step of the flow; false when it named none. */
function follow(data: unknown): boolean {
  const url = nextUrl(data);
  if (!url) return false;
  window.location.assign(url);
  return true;
}

const OFFLINE = "Couldn't sign in. Check your connection and try again.";

function signInAs(name: string, password: string) {
  return name.includes("@")
    ? oauthClient.signIn.email({ email: name, password })
    : oauthClient.signIn.username({ username: name, password });
}

/** MCP client sign-in before using Aria's tools as the person. An existing session continues the
 * authorization; otherwise the server carries on to consent after sign-in. The signed query ties
 * both to the flow (lib/host/oauth-client.ts). */
function OAuthSignInPage() {
  const [checking, setChecking] = useState(true);
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [wrongPair, setWrongPair] = useState(false);
  const identifierRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    void (async () => {
      const session = await oauthClient.getSession();
      if (session.data) {
        const continued = await oauthClient.$fetch("/oauth2/continue", {
          method: "POST",
          body: { selected: true },
        });
        if (!continued.error && follow(continued.data)) return;
        setError(
          continued.error?.message ?? "This sign-in link has expired. Start again from the app.",
        );
      }
      setChecking(false);
    })();
  }, []);

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setPending(true);
    setError(null);
    let result: Awaited<ReturnType<typeof signInAs>>;
    try {
      result = await signInAs(identifier.trim(), password);
    } catch {
      setError(OFFLINE);
      return;
    } finally {
      setPending(false);
    }
    const code = result.error?.code ?? "";
    const mismatch = code.startsWith("INVALID_") && code.endsWith("_OR_PASSWORD");
    setWrongPair(mismatch);
    if (result.error) {
      setError(
        mismatch
          ? "That email or username and password don't match. Check both and try again."
          : (result.error.message ?? OFFLINE),
      );
      identifierRef.current?.focus();
      return;
    }
    if (!follow(result.data))
      setError("Signed in, but this sign-in link has expired. Start again from the app.");
  };

  return (
    <AuthFrame>
      <CardHeader className="items-center text-center">
        <CardTitle asChild className="text-xl">
          <h1>Sign in to connect an app</h1>
        </CardTitle>
        <CardDescription>
          An app such as Claude wants to use Aria&rsquo;s tools as you. Sign in, then you decide
          what it may do.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {checking ? (
          <p className="text-muted-foreground text-center text-sm">Checking your sign-in…</p>
        ) : (
          <form className="space-y-4" onSubmit={(event) => void onSubmit(event)}>
            <div className="space-y-2">
              <Label htmlFor="identifier">Email or username</Label>
              <Input
                ref={identifierRef}
                id="identifier"
                autoComplete="username"
                autoCapitalize="none"
                spellCheck={false}
                aria-invalid={wrongPair ? true : undefined}
                aria-describedby={wrongPair ? "oauth-error" : undefined}
                value={identifier}
                onChange={(event) => setIdentifier(event.target.value)}
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                autoComplete="current-password"
                aria-invalid={wrongPair ? true : undefined}
                aria-describedby={wrongPair ? "oauth-error" : undefined}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                required
              />
            </div>
            <p id="oauth-error" role="alert" className="text-destructive text-sm empty:hidden">
              {error}
            </p>
            <Button className="w-full" disabled={pending} type="submit">
              {pending ? "Signing in…" : "Sign in"}
            </Button>
          </form>
        )}
      </CardContent>
    </AuthFrame>
  );
}
